import type { Dictionary } from '@ngrx/entity';
import type { Uuid } from '@common/types';
import type { NormalizedSectionEntity } from '@web/types';
import type { SectionsState } from './sections.reducer';
import type { PageStructureState } from '../page-structure-feature.reducer';

import { createSelector } from '@ngrx/store';
import { denormalize } from 'normalizr';

import { selectColumnsEntities } from '@web/store/page-structure/columns/columns.selectors';
import { selectGridsEntities } from '@web/store/page-structure/grids/grids.selectors';
import { selectWidgetsEntities } from '@web/store/page-structure/widgets/widgets.selectors';
import { sectionSchema } from '@web/store/page-structure/page-structure/mockStructureData';

import { SECTIONS_FEATURE_KEY, sectionsAdapter } from './sections.reducer';
import { pageStructureState } from '../page-structure-state.selectors';

const { selectAll, selectEntities, selectIds } = sectionsAdapter.getSelectors();

// Lookup the 'Sections' feature state managed by NgRx
export const selectSectionsState = createSelector(
    pageStructureState,
    (state: PageStructureState): SectionsState => state[SECTIONS_FEATURE_KEY]
);

export const selectAllSections = createSelector(selectSectionsState, (state: SectionsState) => selectAll(state));

export const selectSectionsEntities = createSelector(selectSectionsState, (state: SectionsState) =>
    selectEntities(state)
);

export const selectSectionsIds = createSelector(selectSectionsState, (state: SectionsState) => selectIds(state));

export const selectSectionById = (id: Uuid) =>
    createSelector(
        selectSectionsEntities,
        (entities: Dictionary<NormalizedSectionEntity>): NormalizedSectionEntity => entities[id]
    );

export const selectDenormalizedSectionById = (id: Uuid) =>
    createSelector(
        selectSectionsEntities,
        selectColumnsEntities,
        selectGridsEntities,
        selectWidgetsEntities,
        (sections, columns, structures, widgets) => {
            const normalizedStructure = {
                sections,
                columns,
                widgets: {
                    ...widgets,
                    ...structures,
                },
            };

            return denormalize(id, sectionSchema, normalizedStructure);
        }
    );
